import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import mobile from '../assets/images/misc/mobile.png';
import { getMobileApps } from '../services/mobileAppService';

const MobileApps = () => {
    const [appsData, setAppsData] = useState([]);
    const [hasFired, setHasFired] = useState(false);
    const sectionRef = useRef(null);
    const location = useLocation();

    // Trigger fetch if visible
    useEffect(() => {
        if (hasFired) return;

        // On normal home load (no hash), fetch immediately instead of waiting for scroll.
        if (!location.hash) {
            setHasFired(true);
            return;
        }

        const targetId = 'our-apps';

        // 1. Immediate match for target hash
        if (location.hash === `#${targetId}`) {
            setHasFired(true);
            return;
        }

        let observer = null;
        let timer = null;

        const startObserver = () => {
             observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setHasFired(true);
                        observer.disconnect();
                    }
                },
                { threshold: 0.1, rootMargin: '0px' }
            );
            if (sectionRef.current) observer.observe(sectionRef.current);
        };

        // 2. Logic: If there is a hash for SOME OTHER section, we delay
        if (location.hash && location.hash !== `#${targetId}`) {
            timer = setTimeout(startObserver, 2000);
        } else {
            startObserver();
        }

        return () => {
            if (timer) clearTimeout(timer);
            if (observer) observer.disconnect();
        };
    }, [hasFired, location.hash]);

    useEffect(() => {
        if (!hasFired) return;

        const fetchApps = async () => {
            try {
                const data = await getMobileApps();
                console.log("Mobile Apps Fetched:", data);
                setAppsData(data);
            } catch (error) {
                console.error('Error fetching mobile apps data:', error);
            }
        };

        fetchApps();
    }, [hasFired]);

    // Helper to render Link or anchor tag based on the link format
    const renderLink = (link, text) => {
        if (!link) return null;
        if (link.startsWith('/')) {
            return <Link to={link}><strong>{text}</strong></Link>;
        } else {
            return <a href={link} target="_blank" rel="noopener noreferrer"><strong>{text}</strong></a>;
        }
    };

    return (
        <section id="our-apps" className="padding" ref={sectionRef} style={{ minHeight: '500px' }}>
            <div className="container">
                {appsData.length === 0 ? (
                    <div className="text-center">
                        <h3>No Mobile Apps Data Found.</h3>
                        <p>Please check if the data exists in the backend and the API is working.</p>
                    </div>
                ) : (
                    <>
                        <div className="row">
                            <div className="col-lg-6 col-md-7 col-sm-12 pr-lg-5">
                                <div className="heading-title bottom30 wow fadeInLeft" data-wow-delay="200ms">
                                    <span className="darkcolor text-center text-md-left">   </span>
                                    <h2 className="bottom30 text-dark font-normal text-center text-md-left">
                                        {appsData[0].title && appsData[0].title.includes(' ') ? (
                                            <>
                                                <span className="defaultcolor font-bold">{appsData[0].title.split(' ')[0]}</span> {appsData[0].title.split(' ').slice(1).join(' ')}
                                            </>
                                        ) : (
                                            <span className="defaultcolor font-bold">{appsData[0].title}</span>
                                        )}
                                    </h2>
                                </div>
                            </div>
                            <div className="col-lg-6 col-md-5 col-sm-12 wow fadeInRight pl-lg-5" data-wow-delay="200ms">
                                <p className="heading_space mt-n3 mt-sm-0 text-center text-md-left">{appsData[0].short_desc}</p>
                            </div>
                        </div>
                        <div className="row d-flex align-items-center" id="app-feature">
                            {/* Left Column (Items 0-1) */}
                            <div className="col-lg-3 col-md-4 col-sm-12">
                                <div className="text-center text-md-left">
                                    {appsData.slice(0, 2).map((app, index) => (
                                        <div key={app.id} className={`feature-item ${index === 0 ? 'mt-1' : 'mt-5'} wow fadeInLeft`} data-wow-delay={`${200 + index * 50}ms`}>
                                            <div className="icon"><i className={app.icon}></i></div>
                                            <div className="text">
                                                <h4 className="bottom15">
                                                    <span className="d-inline-block">{app.name}</span>
                                                </h4>
                                                <div dangerouslySetInnerHTML={{ __html: app.description }} />
                                                {renderLink(app.button_link, app.button_name)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            
                            {/* Middle Column (Image) */}
                            <div className="col-lg-6 col-md-4 col-sm-12 text-center">
                                <div className="image feature-item d-inline-block wow fadeIn my-5 my-md-0" data-wow-delay="400ms">
                                    <img src={mobile} alt="mobile phones" />
                                </div>
                            </div>

                            {/* Right Column (Items 2-3) */}
                            <div className="col-lg-3 col-md-4 col-sm-12">
                                <div className="text-center text-md-right">
                                     {appsData.slice(2, 4).map((app, index) => (
                                        <div key={app.id} className={`feature-item ${index === 0 ? 'mt-1' : 'mt-5'} wow fadeInRight`} data-wow-delay={`${200 + index * 50}ms`}>
                                            <div className="icon"><i className={app.icon}></i></div>
                                            <div className="text">
                                                <h4 className="bottom15">
                                                    <span className="d-inline-block">{app.name}</span>
                                                </h4>
                                                <div dangerouslySetInnerHTML={{ __html: app.description }} />
                                                {renderLink(app.button_link, app.button_name)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default MobileApps;
